import { chatService } from "@/services/chatService";
import type { TeacherScenario, TeacherScenarioMode, TeacherScenarioStage } from "@/config/teacherScenarios";
import {
  buildKnowledgeExcerpt,
  getKnowledgeProfileForScenario,
  getPrincipalExportFocus,
} from "@/config/teacherScenarioKnowledge";
import type { ChatResponse } from "@/types/chat";
import type { ChatHistoryRecord } from "@/types/history";
import type { UserSession } from "@/types/session";
import type { WorkspacePersona } from "@/config/workspacePersona";
import { plainUserText } from "@/lib/plainUserText";

export interface TeacherScenarioSessionContext {
  scenarioId: string;
  mode: TeacherScenarioMode;
  stage: TeacherScenarioStage;
  complicationIndex?: number;
}

export interface ScenarioChatTurn {
  id?: string;
  role: "user" | "assistant";
  content: string;
  rawMarkdown?: string;
}

const STAGE_MARKER_READY = "[[STAGE:practice]]";
const STAGE_MARKER_DEBRIEF = "[[STAGE:debrief]]";
const GENERATE_TAKEAWAY_MARKER = "[GENERATE_PRINCIPAL_TAKEAWAY]";

export function buildScenarioKnowledgePrefix(
  scenario: TeacherScenario,
  ctx: TeacherScenarioSessionContext,
): string {
  const profile = getKnowledgeProfileForScenario(scenario);
  const excerpt = buildKnowledgeExcerpt(profile);
  const complicationHint =
    ctx.stage === "live" && ctx.complicationIndex !== undefined
      ? scenario.complications[ctx.complicationIndex]
      : null;

  return [
    "[KNOWLEDGE]",
    `category=${scenario.category}`,
    `frameworks=${profile.frameworkIds.join(",")}`,
    `coaching_gates=${scenario.coachingGates.join(" | ")}`,
    complicationHint ? `next_complication_hint=${complicationHint}` : null,
    `principal_export_focus=${getPrincipalExportFocus(scenario)}`,
    excerpt,
  ]
    .filter(Boolean)
    .join("\n");
}

export const COACHING_STYLE_DIRECTIVE = [
  "[COACHING_STYLE]",
  "Keep every reply SHORT — 2 to 4 sentences, about 70 words maximum.",
  "Lead with ONE focused question; draw out the teacher's own thinking before offering anything.",
  "Do NOT give bulleted lists, multiple strategies, or full scripts during coaching.",
  "Guide the teacher toward their own insight — never spoon-feed the complete answer.",
].join("\n");

export function buildScenarioSystemPrefix(
  scenario: TeacherScenario,
  ctx: TeacherScenarioSessionContext,
  options: { includeCoachingStyle?: boolean } = {},
): string {
  const includeCoachingStyle = options.includeCoachingStyle ?? true;
  return [
    "[TEACHER_SCENARIO]",
    `scenario_id=${scenario.id}`,
    `mode=${ctx.mode}`,
    `stage=${ctx.stage}`,
    `title=${scenario.title}`,
    ctx.complicationIndex !== undefined ? `complication_index=${ctx.complicationIndex}` : null,
    buildScenarioKnowledgePrefix(scenario, ctx),
    includeCoachingStyle ? COACHING_STYLE_DIRECTIVE : null,
  ]
    .filter(Boolean)
    .join("\n");
}

export function buildLocalOpeningMessage(scenario: TeacherScenario, mode: TeacherScenarioMode): string {
  const leadQuestion = scenario.coachingGates[0] ?? "What's your opening move?";
  const modeIntro =
    mode === "prep"
      ? "Let's anticipate this moment together — sharpen your language and regulation strategies before you're in the room."
      : "Describe how you'd handle this first. When your plan is solid, we'll role-play the conversation with possible twists.";
  return `${modeIntro}\n\n**${scenario.title}:** ${scenario.openingPrompt}\n\n${leadQuestion}`;
}

export function buildScenarioOpeningMessage(
  scenario: TeacherScenario,
  mode: TeacherScenarioMode,
): string {
  const modeLine =
    mode === "prep"
      ? "The teacher chose PREP mode — help them anticipate and prepare; do not start full role-play unless they ask."
      : "The teacher chose PRACTICE mode — after coaching their plan, move into interactive role-play.";
  return `${buildScenarioSystemPrefix(scenario, { scenarioId: scenario.id, mode, stage: "plan" })}\n\n${modeLine}\n\nScenario context: ${scenario.openingPrompt}\n\nBegin with one focused coaching question about how they would handle this. Do not give the answer first. Reference the situation briefly — the teacher already read the brief.`;
}

export function buildComplicationMessage(
  scenario: TeacherScenario,
  complicationIndex: number,
): string {
  const text = scenario.complications[complicationIndex] ?? scenario.complications[0];
  return `${buildScenarioSystemPrefix(scenario, {
    scenarioId: scenario.id,
    mode: "practice",
    stage: "live",
    complicationIndex,
  })}\n\n[COMPLICATION]\nIntroduce this development naturally in the scenario now: ${text}`;
}

export function buildDebriefMessage(scenario: TeacherScenario, mode: TeacherScenarioMode): string {
  return `${buildScenarioSystemPrefix(scenario, {
    scenarioId: scenario.id,
    mode,
    stage: "debrief",
  })}\n\n[STAGE:debrief]\nMove to debrief. Use these prompts: ${scenario.debriefPrompts.join(" | ")}`;
}

export function buildPrincipalTakeawayPrompt(
  scenario: TeacherScenario,
  mode: TeacherScenarioMode,
  transcript: string,
): string {
  return `${buildScenarioSystemPrefix(scenario, {
    scenarioId: scenario.id,
    mode,
    stage: "debrief",
  }, { includeCoachingStyle: false })}\n\n${GENERATE_TAKEAWAY_MARKER}\n\nThis is a DOCUMENT request — ignore the brief-reply coaching style. Using the conversation transcript below, produce a professional Markdown document ONLY (no surrounding commentary) with these exact headings:\n\n# Scenario Practice Takeaway — ${scenario.title}\n\n## Situation\n## My planned approach\n## What I practiced / prepared\n## Key insights\n## Commitments and next steps\n## If I need support from leadership\n\nTone: professional, concise, suitable for a principal. Write a complete one-page document. ${getPrincipalExportFocus(scenario)}\n\nTranscript:\n${transcript}`;
}

export interface ScenarioQuickAction {
  label: string;
  prompt: string;
  /** When set, the session advances to this stage after the action is sent. */
  advanceTo?: TeacherScenarioStage;
  /** When true, surface the saved-resource panel without requiring debrief stage first. */
  offerResource?: boolean;
}

const WRAP_UP_ACTION: ScenarioQuickAction = {
  label: "Wrap up & create resource",
  prompt: "I'm ready to wrap up and create my saved resource.",
  advanceTo: "debrief",
  offerResource: true,
};

/** Teacher asked for a takeaway / saved resource — offer the document panel, not inline text. */
export function isResourceRequest(message: string): boolean {
  const text = message.trim();
  if (!text) return false;

  return (
    /\b(resource|resources|takeaway|takeaways|one.?pager|handout|hand.?out|saved document|reference (sheet|guide))\b/i.test(
      text,
    ) ||
    /\b(something to (keep|share|bring|take with me))\b/i.test(text) ||
    /\b(share with (my )?(admin|principal|leadership))\b/i.test(text) ||
    /\b(create|generate|get|give me|need|want).{0,40}\b(resource|resources|takeaway|document)\b/i.test(text)
  );
}

/**
 * Short, neutral shortcuts the teacher can fire mid-session. They are brief
 * help-requests (not scripted lines that put words in the teacher's mouth) and
 * adapt to what the coach just said — e.g. an "I'm not sure" option only appears
 * when the coach has asked a question.
 */
export function getScenarioQuickActions(
  stage: TeacherScenarioStage,
  mode: TeacherScenarioMode,
  lastAssistantText?: string,
): ScenarioQuickAction[] {
  const coachAskedQuestion = Boolean(lastAssistantText && lastAssistantText.includes("?"));

  if (stage === "plan") {
    const base: ScenarioQuickAction[] = [];
    if (coachAskedQuestion) {
      base.push({ label: "I'm not sure", prompt: "I'm not sure — help me think it through." });
    }
    base.push(
      { label: "Give me a hint", prompt: "Give me a small hint to get started." },
      { label: "What am I missing?", prompt: "What am I missing here?" },
    );
    if (mode === "practice") {
      base.push({ label: "I'm ready to practice", prompt: "I'm ready to try the role-play." });
    }
    base.push(WRAP_UP_ACTION);
    return base;
  }

  if (stage === "live") {
    const base: ScenarioQuickAction[] = [];
    if (coachAskedQuestion) {
      base.push({ label: "I'm not sure", prompt: "I'm not sure — give me a hint." });
    }
    base.push(
      { label: "How might they respond?", prompt: "How might they respond?" },
      { label: "Help me respond", prompt: "Help me respond." },
      { label: "Pause & reset", prompt: "Let's pause and reset." },
      WRAP_UP_ACTION,
    );
    return base;
  }

  if (stage === "debrief") {
    return [
      { label: "Create my resource", prompt: "", offerResource: true },
      { label: "What did I do well?", prompt: "What did I do well?" },
      { label: "What would I change?", prompt: "What would I change next time?" },
      { label: "One thing to remember", prompt: "Give me one thing to remember." },
    ];
  }

  return [];
}

export function buildModeSwitchMessage(
  scenario: TeacherScenario,
  mode: TeacherScenarioMode,
  stage: TeacherScenarioStage,
): string {
  const prefix = buildScenarioSystemPrefix(scenario, { scenarioId: scenario.id, mode, stage });
  const note =
    mode === "practice"
      ? "[MODE_SWITCH] The teacher switched to PRACTICE mode. Begin or resume the role-play now — embody the other person and respond in character. Keep it brief and end by giving them room to respond."
      : "[MODE_SWITCH] The teacher switched to PREP mode because they want to slow down. Pause any role-play. Help them anticipate, find language, and regulate. Ask one focused question.";
  return `${prefix}\n\n${note}`;
}

export function buildReflectionPrompt(scenario: TeacherScenario): string {
  return `${buildScenarioSystemPrefix(scenario, { scenarioId: scenario.id, mode: "prep", stage: "debrief" })}\n\nHelp me reflect so this practice turns into growth. In a few sentences: name one strength I showed, one concrete thing to try next time, and one sentence I can hold onto in the moment. Keep it warm and specific to what we discussed.`;
}

export function parseStageAdvancement(content: string): TeacherScenarioStage | null {
  if (content.includes(STAGE_MARKER_DEBRIEF)) return "debrief";
  if (content.includes(STAGE_MARKER_READY)) return "live";
  return null;
}

export function stripStageMarkers(text: string): string {
  return text.replace(/\[\[STAGE:(practice|debrief)\]\]/g, "").trim();
}

export function isSubstantivePlan(message: string): boolean {
  const trimmed = message.trim();
  return trimmed.length >= 80 && /\b(i would|i'll|my plan|first|then|approach|say|ask|listen|boundary|calm|student|parent)\b/i.test(trimmed);
}

export function buildDebriefTranscript(
  messages: ScenarioChatTurn[],
  scenario: TeacherScenario,
  mode: TeacherScenarioMode,
): string {
  const lines = [
    `Scenario: ${scenario.title}`,
    `Category: ${scenario.category}`,
    `Mode: ${mode}`,
    `Situation: ${scenario.openingPrompt}`,
    "",
    "Conversation:",
  ];

  for (const msg of messages) {
    if (msg.id === "welcome") continue;
    const text = (msg.rawMarkdown ?? msg.content).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (!text) continue;
    lines.push(`${msg.role === "user" ? "Teacher" : "HeyBodhi"}: ${text}`);
  }

  return lines.join("\n");
}

export async function sendTeacherScenarioMessage({
  message,
  session,
  workspacePersona,
  scenario,
  ctx,
}: {
  message: string;
  session: UserSession;
  workspacePersona: WorkspacePersona;
  scenario: TeacherScenario;
  ctx: TeacherScenarioSessionContext;
}): Promise<ChatResponse> {
  const prefixed = message.trimStart().startsWith("[TEACHER_SCENARIO]")
    ? message
    : `${buildScenarioSystemPrefix(scenario, ctx)}\n\n${message}`;
  return chatService.sendMessage({
    message: prefixed,
    context: "teacher_scenarios",
    session,
    agent_routing: "teacher_scenarios",
    workspace_persona: workspacePersona,
  });
}

export function extractAssistantMarkdown(response: ChatResponse): string {
  return (
    response.content?.plain_text ??
    response.content?.body?.replace(/<[^>]+>/g, " ") ??
    response.agent_response ??
    ""
  ).trim();
}

export function defaultReachedStages(): Record<TeacherScenarioStage, boolean> {
  return { setup: true, plan: true, live: false, debrief: false };
}

/** Pull scenario_id / mode / stage from a stored message that includes [TEACHER_SCENARIO]. */
export function parseScenarioContextFromText(text: string): Partial<TeacherScenarioSessionContext> | null {
  if (!text.includes("[TEACHER_SCENARIO]")) return null;

  const scenarioId = text.match(/scenario_id=([^\s\n]+)/)?.[1];
  const modeRaw = text.match(/mode=(prep|practice)/)?.[1];
  const stageRaw = text.match(/stage=(setup|plan|live|debrief)/)?.[1];

  return {
    ...(scenarioId ? { scenarioId } : {}),
    ...(modeRaw ? { mode: modeRaw as TeacherScenarioMode } : {}),
    ...(stageRaw ? { stage: stageRaw as TeacherScenarioStage } : {}),
  };
}

/** Remove the system prefix from a stored user turn for display in the chat UI. */
export function stripTeacherScenarioUserDisplay(raw: string): string {
  if (!raw.includes("[TEACHER_SCENARIO]")) return plainUserText(raw);

  const lastBreak = raw.lastIndexOf("\n\n");
  if (lastBreak !== -1) {
    const tail = raw.slice(lastBreak + 2).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (tail) return tail;
  }

  return plainUserText(raw);
}

export interface ParsedScenarioSession {
  scenarioId: string;
  mode: TeacherScenarioMode;
  stage: TeacherScenarioStage;
  reachedStages: Record<TeacherScenarioStage, boolean>;
  resourceOfferVisible: boolean;
}

export function parseScenarioSessionFromHistory(rows: ChatHistoryRecord[]): ParsedScenarioSession | null {
  let scenarioId: string | undefined;
  let mode: TeacherScenarioMode = "prep";
  let lastStage: TeacherScenarioStage = "plan";
  let sawLive = false;
  let sawDebrief = false;

  for (const row of rows) {
    const content = row.message.content;
    const ctx = parseScenarioContextFromText(content);
    if (ctx?.scenarioId) scenarioId = ctx.scenarioId;
    if (ctx?.mode) mode = ctx.mode;
    if (ctx?.stage) lastStage = ctx.stage;

    const marker = parseStageAdvancement(content);
    if (marker === "live" || ctx?.stage === "live") sawLive = true;
    if (marker === "debrief" || ctx?.stage === "debrief") sawDebrief = true;
  }

  if (!scenarioId) return null;

  const reachedStages = defaultReachedStages();
  if (sawLive) reachedStages.live = true;
  if (sawDebrief) reachedStages.debrief = true;

  const userMessageCount = rows.filter((r) => r.message.role === "user").length;
  const stage: TeacherScenarioStage = sawDebrief ? "debrief" : sawLive ? "live" : lastStage;

  return {
    scenarioId,
    mode,
    stage,
    reachedStages,
    resourceOfferVisible: sawDebrief && userMessageCount >= 2,
  };
}

export function hydrateScenarioMessagesFromHistory(
  rows: ChatHistoryRecord[],
): Array<ScenarioChatTurn & { id: string; timestamp: Date }> {
  return rows.map((row) => {
    const isUser = row.message.role === "user";
    const raw = row.message.content;
    const timestamp = new Date(row.message.timestamp);

    if (isUser) {
      const visible = stripTeacherScenarioUserDisplay(raw);
      return {
        id: String(row.id),
        role: "user" as const,
        content: visible,
        rawMarkdown: visible,
        timestamp,
      };
    }

    const plain = raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const cleaned = stripStageMarkers(plain);
    return {
      id: String(row.id),
      role: "assistant" as const,
      content: raw,
      rawMarkdown: cleaned,
      timestamp,
    };
  });
}

export function resolveScenarioById(
  scenarioId: string,
  scenarios: TeacherScenario[],
): TeacherScenario | undefined {
  return scenarios.find((s) => s.id === scenarioId);
}

export function buildScenarioSessionName(scenario: TeacherScenario, mode: TeacherScenarioMode): string {
  const modeLabel = mode === "prep" ? "Prep" : "Practice";
  return `${scenario.title} · ${modeLabel}`;
}
